import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  EVClickAwayListener,
  EVPopper,
  EVList,
  EVListItem,
  EVListItemText,
  EVPaper,
  EVListItemIcon,
  EVSignOut,
} from '@eagleview/ev-comp-library';
import { useDispatch } from 'react-redux';
import { signOutUser, signOutGuestToSignIn, wait } from 'utils/app.utils';
import { WAIT_AFTER_REVOKE_TOKEN_API_CALL } from 'constants/AppConstants';
import * as notificationAction from 'features/NotificationsPopup/Notifications.action';
import * as authorizeAction from 'features/authorize/Authorize.actions';
import useStyles from '../UserProfileStyle';

/**
 * A menu component showing user settings
 */
const UserMenu = (props) => {
  const {
    monogramVariant, handleMenuClickAway, open, anchorEl, setViewProfile, userDetails, isImpersonated, isSignOutDisabled, isGuestUser,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { firstName, lastName } = userDetails;

  const revokeAccessToken = () => dispatch(notificationAction.revokeAccessToken());
  const expireUser = () => dispatch(authorizeAction.expireGuid());

  /**
   * Open user profile panel.
   */
  const handleClickOpen = () => {
    setViewProfile(true);
    handleMenuClickAway();
  };

  /**
   * Sign out user. For guest: revoke token API then redirect to /signin?redirect={current resource}. #genai
   */
  const handleSignOut = async () => {
    if (isGuestUser) {
      handleMenuClickAway();
      revokeAccessToken();
      await wait(WAIT_AFTER_REVOKE_TOKEN_API_CALL);
      const currentResource = window.location.pathname + window.location.search;
      signOutGuestToSignIn(currentResource);
      return;
    }
    if (!isImpersonated) {
      handleMenuClickAway();
      /**
       * @description api call to revoke the access token
       */
      revokeAccessToken();
      await wait(WAIT_AFTER_REVOKE_TOKEN_API_CALL);
      dispatch(authorizeAction.setIsShareToken(false));
      signOutUser();
    } else {
      expireUser();
    }
    dispatch(authorizeAction.setSignOutDisabled(true));
  };

  return (
    <EVPopper
      id="userContextMenu"
      className={classes.popper}
      placement={monogramVariant === 'medium' ? 'bottom-start' : 'right-start'}
      anchorEl={anchorEl}
      open={open}
    >
      <EVClickAwayListener onClickAway={handleMenuClickAway}>
        <EVPaper className={clsx({ [classes.settingsOnSide]: monogramVariant === 'small' },
          { [classes.settingsBelow]: monogramVariant === 'medium' },
          classes.userMenu)}
        >
          <EVList>
            {!isGuestUser && (
              <EVListItem id="userSettings" onClick={() => handleClickOpen()} className={classes.listStyle}>
                <EVListItemText primary={t('UserSettings')} />
              </EVListItem>
            )}
            <EVListItem id="signOut" className={classes.listStyle} onClick={handleSignOut} disabled={isSignOutDisabled}>
              <EVListItemIcon className={classes.listItemIcon}>
                <EVSignOut className={classes.signOutIcon} />
              </EVListItemIcon>
              <EVListItemText className={classes.signOutText}>
                {isImpersonated ? (
                  <>
                    <span>{t('SignOutFromImpersonation')}</span>
                    <span className={classes.capitalize}>{` ${firstName} ${lastName}`}</span>
                  </>
                ) : (
                  <span>{t('SignOut')}</span>
                )}
              </EVListItemText>
            </EVListItem>
          </EVList>
        </EVPaper>
      </EVClickAwayListener>
    </EVPopper>
  );
};

UserMenu.defaultProps = {
  anchorEl: null,
  setViewProfile: null,
};

UserMenu.propTypes = {
  /**
   * Variant of monogram
   * @property {string} monogramVariant
   */
  monogramVariant: PropTypes.oneOf(['micro', 'small', 'medium']).isRequired,

  /**
   * Function to be called on clicking outside the menu
   * @property {string} handleMenuClickAway
   */
  handleMenuClickAway: PropTypes.func.isRequired,

  /**
   * Toggles opening and closing of the menu
   * @property {bool} open
   */
  open: PropTypes.bool.isRequired,

  /**
   * Reference object for EVPopper
   * @property {element/object/func} anchorEl
   */
  anchorEl: PropTypes.oneOfType([PropTypes.element, PropTypes.object, PropTypes.func]),

  /**
   * function to show the view profile panel
   * @property {function} setViewProfile
   */
  setViewProfile: PropTypes.func,

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,

  /**
   * store value indicating whether the user is impersonated or not
   * @property {Boolean} isImpersonated
   */
  isImpersonated: PropTypes.bool.isRequired,

  /**
   * store value indicating whether the sign out button is disabled or not
   * @property {Boolean} isSignOutDisabled
   */
  isSignOutDisabled: PropTypes.bool.isRequired,

  /**
   * When true, show only Sign out in menu and redirect to /signin?redirect=... on sign out. #genai
   * @property {Boolean} isGuestUser
   */
  isGuestUser: PropTypes.bool,
};

UserMenu.defaultProps = {
  ...UserMenu.defaultProps,
  isGuestUser: false,
};

export default UserMenu;
